import { cloneDeep } from 'lodash-es';
import { EventPeriod, Line } from '@/constants';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { LiveItem } from '@/models/live';
import { createLivePageHeaders } from '@/pages/events/live/LivePageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { liveService } from '@/services/event/liveService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import { ILiveFilters } from '@/types/event/live';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'eventDate', order: 'asc' as const }];

function createLiveStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<LiveItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<ILiveFilters>({
        sportId: createRequiredSportFilter(),
        eventPeriodId: {
            value: EventPeriod.FullTime,
            title: 'pregame.eventPeriodId',
            allowSaveState: true,
            disableClear: true,
        },
        eventSearch: {
            value: null,
            title: 'common.search',
            allowSaveState: false,
        },
        bookmakerId: {
            value: null,
            title: 'common.bookmakers',
            allowSaveState: true,
        },
        customLeagueLimitsCategoryId: {
            value: null,
            title: 'pregame.leagueLimitsCategory',
            allowSaveState: true,
        },
        line: {
            value: Line.Main,
            title: 'pregame.line',
            allowSaveState: true,
            disableClear: true,
        },
        customLeagueId: {
            value: null,
            title: 'common.customLeague',
            allowSaveState: true,
        },
        customTeamId: {
            value: null,
            title: 'common.customTeam',
            allowSaveState: true,
        },
        invertedEvents: {
            value: null,
            title: 'pregame.inverted',
            allowSaveState: true,
        },
        eventExcludedFromFeed: {
            value: null,
            title: 'pregame.excluded',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.eventPeriodId.value = EventPeriod.FullTime;
        filters.eventSearch.value = null;
        filters.bookmakerId.value = null;
        filters.customLeagueLimitsCategoryId.value = null;
        filters.line.value = Line.Main;
        filters.customLeagueId.value = null;
        filters.customTeamId.value = null;
        filters.invertedEvents.value = null;
        filters.eventExcludedFromFeed.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createLivePageHeaders(filters.sportId.value!));

    const availableColumns = computed(() => headers.value.filter((item) => item.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    watch(
        () => filters.sportId.value,
        () => {
            hiddenColumns.value = [];
        }
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'live',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            let filtersCopy = cloneDeep(filters);

            const params = {
                filters: filtersCopy as ILiveFilters,
                options: {
                    sortBy: options.value.sortBy,
                    rowsPerPageItems: options.value.rowsPerPageItems,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country'],
            };

            const response = await liveService.list(params);
            items.value = response.data.rows.map((item) => new LiveItem(item));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function useLiveStore(storeId: string) {
    const useStore = defineStore(storeId, () => createLiveStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'live'),
    });
    return useStore();
}

export type LiveStoreSetup = ReturnType<typeof useLiveStore>;
